import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Target, Eye, Users, Trophy } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { fileUrl, usePageTitle } from "@/lib/api";
import { COMMUNITIES, IMG, SITE } from "@/lib/content";
import { PageHero, Reveal, SectionHeading } from "@/components/site/Bits";

const Block = ({ icon: Icon, title, children, testid }) => (
  <Reveal className="card-navy p-7 sm:p-9" >
    <div data-testid={testid}>
      <Icon className="h-8 w-8 text-gold" />
      <h3 className="mt-5 font-display text-3xl font-black uppercase italic">{title}</h3>
      <div className="mt-3 space-y-3 text-sm sm:text-base text-slate-300 leading-relaxed">{children}</div>
    </div>
  </Reveal>
);

const Leadership = () => {
  const { settings } = useSite();
  return (
    <section className="bg-navy py-20 sm:py-28" data-testid="about-leadership">
      <div className="wrap grid gap-12 lg:grid-cols-[0.9fr_1.1fr] items-center">
        <Reveal>
          <div className="relative aspect-[4/5] bg-pitch overflow-hidden border border-line">
            {settings.coach_kp_photo ? (
              <img src={fileUrl(settings.coach_kp_photo)} alt="Coach KP" className="h-full w-full object-cover" />
            ) : (
              <div className="absolute inset-0 stripes flex items-end p-8">
                <p className="font-display text-[9rem] font-black italic leading-none text-stroke">KP</p>
              </div>
            )}
            <div className="absolute left-0 top-6 bg-gold px-4 py-2 font-display font-extrabold uppercase text-navy">Leadership</div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="eyebrow mb-4">President / Program Director</p>
          <h2 className="h-display text-6xl sm:text-7xl">Coach KP</h2>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed whitespace-pre-line" data-testid="coach-kp-bio">{settings.coach_kp_bio}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href={SITE.tel} className="btn-gold" data-testid="about-call-button"><Phone className="h-4 w-4" /> {SITE.phone}</a>
            <a href={SITE.mailto} className="btn-line" data-testid="about-email-button"><Mail className="h-4 w-4" /> Email Coach KP</a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function About() {
  usePageTitle("About");
  const { settings } = useSite();
  return (
    <div data-testid="about-page">
      <PageHero testid="about-hero" eyebrow="About Tri County Allstars" title={<>Different Parks.<br /><span className="text-gold">One Coast.</span></>} image={IMG.huddle}
        sub="An independent postseason youth football organization bringing together standout athletes from programs across Florida's Treasure Coast." />

      <section className="bg-pitch py-20 sm:py-28">
        <div className="wrap grid gap-5 md:grid-cols-2">
          <Block icon={Users} title="Who We Are" testid="about-who-we-are">
            <p>Tri County Allstars is an independent postseason youth football organization representing Florida's Treasure Coast.</p>
            <p>Athletes who compete against each other during the regular season come together under one banner — to represent their parks, their families and their communities on a bigger stage.</p>
          </Block>
          <Block icon={Target} title="Our Mission" testid="about-mission">
            <p>{settings.mission_text}</p>
          </Block>
          <Block icon={Eye} title="Our Vision" testid="about-vision">
            <p>We believe the Treasure Coast has some of the best young football talent in Florida. Our vision is to give those athletes a platform to showcase their abilities — and to make the Treasure Coast a name respected on the national All-Star stage.</p>
          </Block>
          <Block icon={Trophy} title="The Tri County Experience" testid="about-experience">
            <p>Fully padded tryouts. A selection process built on effort and ability. Postseason practices, two tournament weekends, and the chance to compete in the AUG National Allstars Classic.</p>
            <p>Athletes leave with new teammates, new competition and memories of representing their coast together.</p>
          </Block>
        </div>
      </section>

      <Leadership />

      <section className="relative bg-pitch py-20 sm:py-28 overflow-hidden" data-testid="about-communities">
        <img src={IMG.coast} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative wrap">
          <SectionHeading eyebrow="Community Representation" title="One Team. Many Hometowns." sub="Our roster is built from programs across the Treasure Coast, including these communities and the surrounding areas." />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-px bg-line/60">
            {COMMUNITIES.map((c, i) => (
              <Reveal key={c} delay={i * 0.05} className="bg-pitch/90 p-6 sm:p-8">
                <MapPin className="h-5 w-5 text-gold" />
                <p className="mt-3 font-display text-2xl sm:text-4xl font-black uppercase italic">{c}</p>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-sm text-slate-400">+ Surrounding Treasure Coast communities</p>
          <Link to="/tryouts" className="btn-gold mt-10" data-testid="about-tryouts-button">Register for Tryouts</Link>
        </div>
      </section>
    </div>
  );
}
